import React from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import Row from '../../bootstrap/component/Row'
import Column from '../../bootstrap/component/Column'
import ColumnSelector from './ColumnSelector'
import { formatDate, formatDateTime } from '../../../util/datetime'

const moment = require('moment')

export const applyFilter = (rowList, columnList, filterValue) => {
    if (!filterValue) {
        return rowList
    }
    if (filterValue && filterValue.toLocaleLowerCase) {
        filterValue = filterValue.toLocaleLowerCase()
    }

    return R.filter(row => {
        let matched = false

        R.forEach(({ property, renderer }) => {
            let value = renderer ? renderer(row, property) : row[property]

            if (value && value.toLocaleLowerCase) {
                value = value.toLocaleLowerCase()
            }

            if (value || value === 0) {
                value += ''
                matched = matched || value.indexOf(filterValue) !== -1
            }
        }, columnList)

        return matched
    }, rowList)
}

const toLocaleLowerCase = value => {
    if (!value && value !== 0) {
        return value
    }
    if (!value.toLocaleLowerCase) {
        return value
    }
    return value.toLocaleLowerCase()
}

export const getColumnByProperty = (columnList, property) => {
    return R.find(R.propEq('property', property))(columnList)
}

export const getColumnValue = (row, column, property) => {
    if (column.renderer) {
        return column.renderer(row, property)
    }
    const value = row[property]
    if (value === 0) {
        return '0'
    }
    if (!value) {
        return null
    }
    return value
}

export const getColumnValueFromColumnList = (row, columnList, property) => {
    const column = getColumnByProperty(columnList, property)
    return getColumnValue(row, column, property)
}

export const getColumnFilterType = (columnList, property) => {
    const column = getColumnByProperty(columnList, property)
    return column.filterType;
}

export const applyColumnFilter = (rowList, columnList, filterValueList) => {
    return R.filter(row => {
        let matched = true

        R.forEachObjIndexed((columnFilterValue, property) => {
            if (!columnFilterValue || !matched) {
                return
            }
            columnFilterValue = '' + toLocaleLowerCase(columnFilterValue)

            let columnValue = getColumnValueFromColumnList(
                row,
                columnList,
                property
            )

            let columnFilterType = getColumnFilterType(
                columnList,
                property
            )

            if (columnValue === null) {
                matched = false
                return
            }
            columnValue = '' + toLocaleLowerCase(columnValue)

            if (columnFilterType === 'select') {
                matched = columnValue === columnFilterValue;
                return
            }

            matched = columnValue.indexOf(columnFilterValue) !== -1
        }, filterValueList)
        return matched
    }, rowList)
}

export const defaultComparator = property => (a, b, column) => {
    let aValue = column.sortValue ? column.sortValue(a) : a[property]
    let bValue = column.sortValue ? column.sortValue(b) : b[property]
    if (!aValue && !bValue) {
        return 0
    }
    if (!aValue && bValue) {
        return -1
    }

    if (aValue && !bValue) {
        return 1
    }

    if (typeof aValue === 'string' || aValue instanceof String) {
        aValue = aValue.toLocaleLowerCase()
    }
    if (typeof bValue === 'string' || bValue instanceof String) {
        bValue = bValue.toLocaleLowerCase()
    }

    if (aValue < bValue) {
        return -1
    }

    if (aValue > bValue) {
        return 1
    }
    return 0
}

export const applySort = (rowList, sortOrder, column) => {
    if (!column) {
        return
    }
    const comparator = column.comparator || defaultComparator(column.property)

    rowList.sort((a, b) => {
        return sortOrder * comparator(a, b, column)
    })
}

export const extractDisplayRows = (rowList, range) => {
    const displayRowList = []
    for (let index = range.startIndex; index < range.endIndex; index++) {
        displayRowList.push(rowList[index])
    }
    return displayRowList
}

export const calculateRange = (rowListLength, rowsPerPage, currentPage) => {
    const range = {
        currentPage: parseInt(currentPage, 10),
        rowsPerPage,
        startIndex: 0,
        endIndex: rowListLength,
        totalPages: 1,
    }

    if (rowsPerPage === 0) {
        range.currentPage = 0
        return range
    }

    if (rowListLength < rowsPerPage) {
        range.currentPage = 0
        range.startIndex = 0
        range.endIndex = rowListLength
        range.totalPages = 1
        return range
    }

    range.totalPages = Math.ceil(rowListLength / rowsPerPage)

    if (rowsPerPage * currentPage >= rowListLength) {
        const rest = rowListLength % rowsPerPage
        const page = (rowListLength - rest) / rowsPerPage
        range.currentPage = rest !== 0 ? page : page - 1
    }

    range.startIndex = rowsPerPage * range.currentPage
    range.endIndex = range.startIndex + rowsPerPage

    if (range.endIndex > rowListLength) {
        range.endIndex = rowListLength
    }

    return range
}

class Table extends React.Component {
    constructor (props) {
        super(props)
        this.state = this.calculateState(
            this.props.rowList,
            this.props.tableSetting
        )
        this.changeSortOrder = this.changeSortOrder.bind(this)
        this.displayPage = this.displayPage.bind(this)
        this.rowsPerPageChanged = this.rowsPerPageChanged.bind(this)
        this.onRowClick = this.onRowClick.bind(this)
        this.onRowDoubleClick = this.onRowDoubleClick.bind(this)
        this.changeFilterValue = this.changeFilterValue.bind(this)
        this.exportCSV = this.exportCSV.bind(this)
    }

    componentWillReceiveProps (nextProps) {
        const state = this.calculateState(
            nextProps.rowList,
            nextProps.tableSetting
        )
        this.setState(state)
    }

    changeSortOrder (sortColumnName) {
        const { tableSetting } = this.props
        const sortOrder =
            tableSetting.sortColumnName === sortColumnName
                ? tableSetting.sortOrder * -1
                : 1
        this.props.changeTableSetting('sortOrder', sortOrder)
        this.props.changeTableSetting('sortColumnName', sortColumnName)
    }

    rowsPerPageChanged (event) {
        this.props.changeTableSetting(
            'rowsPerPage',
            parseInt(event.target.value, 10)
        )
    }

    displayPage (currentPage) {
        this.props.changeTableSetting('currentPage', currentPage)
    }

    changeFilterValue (property, value) {
        const { tableSetting } = this.props

        this.props.changeTableSetting('filterValueList', {
            ...tableSetting.filterValueList,
            [property]: value,
        })
    }

    onRowClick (row) {
        if (this.props.onRowClick) {
            this.props.onRowClick(row)
        }
    }

    onRowDoubleClick (row) {
        if (this.props.onRowDoubleClick) {
            this.props.onRowDoubleClick(row)
        }
    }

    exportCSV () {
        const { rowList, columnList, tableSetting } = this.props

        const filteredRowList = applyColumnFilter(
            rowList,
            columnList,
            tableSetting.filterValueList
        )

        const sortColumn = R.find(
            R.propEq('property', tableSetting.sortColumnName)
        )(columnList)
        applySort(filteredRowList, tableSetting.sortOrder, sortColumn)

        const headerArray = []
        R.forEach(({ header }) => {
            headerArray.push(header)
        }, columnList)

        let csv = headerArray.join(';') + '\r\n'

        R.forEach(row => {
            const rowList = []

            R.forEach(({ renderer, property }) => {
                let value = renderer ? renderer(row, property) : row[property]
                if (value && value.replace) {
                    value = value.replace(';', ',')
                }
                rowList.push(value)
            }, columnList)

            csv += rowList.join(';') + '\r\n'
        }, filteredRowList)

        const blob = new Blob([csv])

        this.downloadTag.href = window.URL.createObjectURL(blob, {
            type: 'text/plain',
        })
        this.downloadTag.click()
    }

    calculateState (rowList, tableSetting) {
        const filteredRowList = applyColumnFilter(
            rowList,
            this.props.columnList,
            tableSetting.filterValueList
        )

        const sortColumn = R.find(
            R.propEq('property', tableSetting.sortColumnName)
        )(this.props.columnList)
        applySort(filteredRowList, tableSetting.sortOrder, sortColumn)

        const range = calculateRange(
            filteredRowList.length,
            tableSetting.rowsPerPage,
            tableSetting.currentPage
        )

        return {
            ...range,
            filteredRowList: extractDisplayRows(filteredRowList, range),
            filteredRowListLength: filteredRowList.length,
            sortOrder: tableSetting.sortOrder,
            sortColumnName: tableSetting.sortColumn,
            filterValueList: tableSetting.filterValueList,
            hiddenColumns: tableSetting.hiddenColumns || []
        }
    }

    render () {
        const { tableSetting } = this.props

        const options = tableSetting.rowsTotalOptions || [20, 40];

        return (
            <div>
                <Row>
                    <Column size={2}>
                        <select
                            className="form-control"
                            onChange={this.rowsPerPageChanged}
                            value={this.state.rowsPerPage}
                        >
                            {options.map((option) => <option key={option} value={option}>{option}</option>)}
                            <option value="0">all</option>
                        </select>
                    </Column>
                    <Column size={1}>
                        {/* <button className="btn btn-primary btn-sm" onClick={this.exportCSV}> */}
                        {/* <i className="fa fa-file-excel-o"/> */}
                        {/* </button> */}
                    </Column>
                    <Column size={5} className="default-text number-status">
                        Displaying {this.state.filteredRowListLength} of{' '}
                        {this.props.rowList.length} Total
                        <br />
                        <a
                            ref={downloadTag => {
                                this.downloadTag = downloadTag
                            }}
                            download="export.csv"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'none' }}
                        >
                            link
                        </a>
                    </Column>

                    <Column size={4} className="text-right">
                        {this.props.sync && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={this.props.sync}
                            >
                                <i className="fa fa-refresh" />
                            </button>
                        )}

                        {this.props.toggleColumnVisibility && (<ColumnSelector
                            title={'Columns'}
                            columnList={this.props.columnList}
                            toggleColumnVisibility={this.props.toggleColumnVisibility}
                            hiddenColumns={tableSetting.hiddenColumns}
                        />)}
                    </Column>
                </Row>
                <Row top="M">
                    <Column size={12}>
                        <table className="default-table">
                            <TableHeader
                                columnList={this.props.columnList}
                                sortColumnName={tableSetting.sortColumnName}
                                sortOrder={tableSetting.sortOrder}
                                filterValueList={tableSetting.filterValueList}
                                changeFilterValue={this.changeFilterValue}
                                changeSortOrder={this.changeSortOrder}
                                hiddenColumns={tableSetting.hiddenColumns}
                            />
                            <TableBody
                                columnList={this.props.columnList}
                                rowList={this.state.filteredRowList}
                                idProperty={this.props.idProperty}
                                onRowClick={this.onRowClick}
                                onRowDoubleClick={this.onRowDoubleClick}
                                hiddenColumns={tableSetting.hiddenColumns}
                            />
                        </table>
                    </Column>
                </Row>
                <Row>
                    <Column size={12}>
                        <div style={{ textAlign: 'right' }}>
                            <Pagination
                                {...this.state}
                                displayPage={this.displayPage}
                            />
                        </div>
                    </Column>
                </Row>
            </div>
        )
    }
}

Table.SORT_ORDER_DESC = -1
Table.SORT_ORDER_ASC = 1

Table.dateComparator = property => (a, b) => {
    a = a[property]
    b = b[property]
    if (!a && !b) {
        return 0
    }
    if (!a && b) {
        return -1
    }
    if (a && !b) {
        return 1
    }

    return moment(a).unix() - moment(b).unix()
}

Table.dateTimeRenderer = property => object => {
    return formatDateTime(object[property])
}

Table.dateRenderer = property => object => {
    return formatDate(object[property])
}

export const formatFollowUpType = (followUpType) => {
    if (!followUpType) {
        return ''
    }
    const labelMap = {
        'In-Clinic-Follow-Up': 'In-Clinic Follow-Up (ICSTP1)',
        'Expert-Call-Follow-Up': 'Expert Call Follow-Up (ICSTP2)',
        'No-Follow-Up': 'No Follow-Up booked (ICSTP3)',
        'No-Pitch': 'No Pitch and Follow-Up (ICSTP4)',
    }
    if (Object.keys(labelMap).includes(followUpType)) {
        return labelMap[followUpType]
    } else {
        return followUpType
    }
}

Table.followUpTypeRenderer = property => object => {
    return formatFollowUpType(object[property])
}

Table.defaultProps = {
    sortOrder: 1,
    sortColumn: null,
    rowsPerPage: 20,
    currentPage: 0,
    filterValue: null,
}

Table.propTypes = {
    idProperty: PropTypes.string.isRequired,
    onRowDoubleClick: PropTypes.func,
    onRowClick: PropTypes.func,
    changeTableSetting: PropTypes.func.isRequired,
    tableSetting: PropTypes.object.isRequired,
}

const TableHeader = ({
    columnList,
    sortColumnName,
    sortOrder,
    changeSortOrder,
    filterValueList,
    changeFilterValue,
    hiddenColumns
}) => {
    return (
        <thead>
            <tr>
                {columnList
                    .filter(column => !hiddenColumns.includes(column.property))
                    .map(column => {
                    const width = column.width ? column.width : ''
                    return (
                        <th
                            width={width}
                            onClick={() => {
                                changeSortOrder(column.property)
                            }}
                            key={column.property}
                        >
                            {column.header}&#160;
                            <SortIcon
                                sortColumnName={sortColumnName}
                                property={column.property}
                                sortOrder={sortOrder}
                            />
                        </th>
                    )
                })}
            </tr>
            <tr className="table-filter-row">
                {columnList
                    .filter(column => !hiddenColumns.includes(column.property))
                    .map(({ property, filterType, filterOptions }) => {
                    return (
                        <th key={property}>
                            <ColumnFilter
                                changeFilterValue={changeFilterValue}
                                filterValueList={filterValueList}
                                type={filterType}
                                options={filterOptions}
                                property={property}
                            />
                        </th>
                    )
                })}
            </tr>
        </thead>
    )
}

const ColumnFilter = ({
    changeFilterValue,
    filterValueList,
    property,
    type = 'input',
    options = [],
}) => {
    if (type === 'select') {
        return (
            <select
                style={{
                    height: '34px',
                    width: '100%',
                    backgroundColor: '#fff',
                    border: 0,
                    outline: 'none',
                    fontWeight: 'normal',
                }}
                onChange={event => {
                    changeFilterValue(property, event.currentTarget.value)
                }}
                value={filterValueList[property] || ''}
            >
                <option value="">All</option>
                {options.map(({ id, value }) => (
                    <option key={id} value={id}>
                        {value}
                    </option>
                ))}
            </select>
        )
    } else {
        return (
            <div className="inner-addon left-addon right-addon">
                <i className="fa fa-filter" />
                <i
                    className="fa fa-times-circle"
                    onClick={() => {
                        changeFilterValue(property, '')
                    }}
                />
                <input
                    type="text"
                    className="form-control"
                    onChange={event => {
                        changeFilterValue(property, event.currentTarget.value)
                    }}
                    value={filterValueList[property] || ''}
                />
            </div>
        )
    }
}
const SortIcon = ({ sortColumnName, property, sortOrder }) => {
    if (sortColumnName !== property) {
        return <i className="fa fa-sort sortIcon" />
    }

    if (sortOrder === Table.SORT_ORDER_ASC) {
        return <i className="fa fa-sort-amount-asc sortIcon" />
    }
    return <i className="fa fa-sort-amount-desc sortIcon" />
}

const TableBody = ({
    columnList,
    rowList,
    idProperty,
    onRowClick,
    onRowDoubleClick,
    hiddenColumns,
}) => {
    return (
        <tbody>
            {rowList.map((row, i) => {
                return (
                    <TableRow
                        columnList={columnList}
                        row={row}
                        key={`${row[idProperty]}${i}`}
                        onRowClick={onRowClick}
                        onRowDoubleClick={onRowDoubleClick}
                        hiddenColumns={hiddenColumns}
                    />
                )
            })}
        </tbody>
    )
}

const TableRow = ({ row, columnList, onRowClick, onRowDoubleClick, hiddenColumns }) => {
    return (
        <tr
            className="table-row"
            onClick={() => {
                onRowClick(row)
            }}
            onDoubleClick={() => {
                onRowDoubleClick(row)
            }}
        >
            {columnList
                .filter(column => !hiddenColumns.includes(column.property))
                .map(({ property, renderer }) => {
                const value = renderer ? renderer(row, property) : row[property]
                return <td key={property}>{value}</td>
            })}
        </tr>
    )
}

class Pagination extends React.Component {
    constructor (props) {
        super(props)
        this.state = this.calculateState(props)
    }

    componentWillReceiveProps (nextProps) {
        this.setState(this.calculateState(nextProps))
    }

    calculateState (props) {
        const pageList = []
        const startIndex = props.currentPage - 5 > 0 ? props.currentPage - 5 : 0
        const endIndex =
            startIndex + 10 < props.totalPages
                ? startIndex + 10
                : props.totalPages

        for (let index = startIndex; index < endIndex; index++) {
            const className = index === props.currentPage ? 'active' : ''
            pageList.push({
                className,
                page: index,
                displayPage: index + 1,
            })
        }
        return {
            pageList,
        }
    }

    render () {
        return (
            <ul className="pagination">
                {this.state.pageList.length > 1 &&
                    this.state.pageList.map(page => {
                        return (
                            <li
                                key={page.page}
                                onClick={() => {
                                    this.props.displayPage(page.page)
                                }}
                                className={page.className}
                            >
                                {page.displayPage}
                            </li>
                        )
                    })}
            </ul>
        )
    }
}

export default Table



// WEBPACK FOOTER //
// ./src/modules/form/component/Table.js